import JsonURL from '@jsonurl/jsonurl'
import isEmpty from 'lodash/isEmpty'
import { z } from 'zod'
import {
  GridFilterModel,
  GridFilterItem,
  GridFilterItemSchema,
  GridLogicOperatorSchema,
  isGridFilterItemWithValue,
  SupportedFilterItemOperatorSchema,
} from '@omnea-app/packages/universal/tables/gridModel'

export function serializeFilterModel(model: GridFilterModel): string {
  const compactModel: SerializedGridFilterModel = {
    i: model.items.map(serializeFilterItem),
    q: model.quickFilterValues ?? [],
    ...(model.logicOperator ? { o: model.logicOperator } : {}),
  }
  if (isEmpty(compactModel.i) && isEmpty(compactModel.q)) {
    return ''
  }
  return JsonURL.stringify(compactModel) ?? ''
}

export function deserializeFilterModel(encoded?: string): GridFilterModel | null {
  try {
    if (isEmpty(encoded) || encoded === undefined) {
      return null
    }

    const parsed = JsonURL.parse(encoded, { noEmptyComposite: true })

    const result = SerializedGridFilterModelSchema.safeParse(parsed)
    if (!result.success) {
      console.warn('Failed to validate filterModel schema', result.error)
      return null
    }

    const compactModel = result.data

    if (compactModel === undefined || (isEmpty(compactModel.i) && isEmpty(compactModel.q))) {
      return null
    }

    const items = compactModel.i.flatMap((tuple, index): GridFilterItem[] => {
      const [field, operator] = tuple
      const value = tuple.length === 3 ? tuple[2] : undefined
      const parsed = GridFilterItemSchema.safeParse({ id: index, field, operator, value })
      if (!parsed.success) {
        console.warn('Failed to parse deserialized filter item', { error: parsed.error })
        return []
      }
      return [parsed.data]
    })

    if (isEmpty(items) && isEmpty(compactModel.q)) {
      return null
    }

    const gridFilterModel: GridFilterModel = { items }

    if (compactModel.q !== undefined && !isEmpty(compactModel.q)) {
      gridFilterModel.quickFilterValues = compactModel.q
    }

    if (compactModel.o) {
      gridFilterModel.logicOperator = compactModel.o
    }

    return gridFilterModel
  } catch (err) {
    console.warn('Failed to deserialize filterModel', err)
    return null
  }
}

const SerializedFilterItemSchema = z.union([
  z.tuple([z.string(), SupportedFilterItemOperatorSchema, z.union([z.string(), z.array(z.string()), z.number()])]),
  z.tuple([z.string(), SupportedFilterItemOperatorSchema]),
])

const SerializedGridFilterModelSchema = z.object({
  i: z.array(SerializedFilterItemSchema),
  q: z.array(z.string()).optional().default([]),
  o: GridLogicOperatorSchema.optional(),
})

export type SerializedGridFilterModel = z.infer<typeof SerializedGridFilterModelSchema>
type SerializedFilterItem = z.infer<typeof SerializedFilterItemSchema>

const serializeFilterItem = (item: GridFilterItem): SerializedFilterItem => {
  if (!isGridFilterItemWithValue(item)) {
    return [item.field, item.operator]
  }
  if (item.value === undefined) {
    return [item.field, item.operator]
  }
  return [item.field, item.operator, item.value]
}
